import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Building2, Plus, X, Loader2, MapPin, Upload, Mail, ChevronDown, Calendar, Edit
} from "lucide-react";
import { useSuperAdmin } from "../../context/SuperAdminContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const COLOR = "3B82F6";

const getToken = () => localStorage.getItem("superadmintoken");
const getHeaders = () => ({ Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" });

const QUALIFICATIONS = [
  "No Formal Education", "10th", "12th", "Graduation",
  "P.G. (Postgraduates)", "Diploma", "ITI",
];

const EMPTY_FORM = {
  name: "",
  logo: null,
  companyLogoPreview: null,
  address: "",
  latitude: "",
  longitude: "",
  toReportEmails: [],
  HourlyRate: "",
  FoodRate: "",
  BusRate: "",
  isActive: true,
  location: "",
  contactPersonName: "",
  contactPersonDesignation: "",
  contactPersonNumber: "",
  contactPersonEmail: "",
  localWorkersAllowed: null,
  allowNewWorkers: null,        
  allowVerifiedWorkersOnly: null, 
  allowPartialHours:null,
  disabledPincodes: [],
  minimumQualification: "",
};

// ── Reusable Field Wrapper ─────────────────────────────────────────────────────
const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-800 mb-2">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {children}
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

const Input = ({ type = "text", hasError, ...props }) => (
  <input
    type={type}
    {...props}
    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-7 text-sm ${
      hasError ? "border-red-400 bg-red-50" : "border-gray-300"
    }`}
  />
);

// ── Wage History Modal ─────────────────────────────────────────────────────────
function WageHistoryModal({ company, onClose }) {
  const [tab, setTab] = useState("company");
  const [history, setHistory] = useState([]);
  const [foodpricehistory, setfoodpricehistory] = useState([]);
  const [buspricehistory, setbuspricehistory] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDept, setSelectedDept] = useState(null);
  const [currentprice, setcurrentprice] = useState("");

  useEffect(() => {
    setLoading(true);
    setHistory([]);
    axios
      .post(
        `${BASE_URL}/onDemandCompanies/price-history`,
        { entityType: "company", entityId: company.id },
        { headers: getHeaders() }
      )
      .then((res) => {
        if (res?.data?.success) {
          setcurrentprice(res?.data?.currentPrice);
          setHistory(res?.data?.history || []);
          setDepartments(res?.data?.departments || []);
        } else {
          toast.error(res?.data?.message || "Failed to load history");
        }
      })
      .catch(() => toast.error("Failed to load history"))
      .finally(() => setLoading(false));
  }, [company.id]);

  useEffect(() => {
    if (!company?.id) return;
    setLoading(true);
    setbuspricehistory([]);
    setfoodpricehistory([]);
    axios
      .get(`${BASE_URL}/onDemandCompanies/bus-food-price-history/${company.id}`, { headers: getHeaders() })
      .then((res) => {
        if (res?.data?.success) {
          setbuspricehistory(res?.data?.busHistory || []);
          setfoodpricehistory(res?.data?.foodHistory || []);
        } else {
          toast.error(res?.data?.message || "Failed to load history");
        }
      })
      .catch(() => toast.error("Failed to load history"))
      .finally(() => setLoading(false));
  }, [company?.id]);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  const formatDateandtime = (iso) =>
    new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });  

    

  const PriceTable = ({ data }) =>
    data.length === 0 ? (
      <p className="text-sm text-gray-400 text-center py-10">No history found.</p>
    ) : (
      <table className="w-full text-sm border-collapse">
        <thead className="sticky top-0 bg-white shadow-md z-[50]">
          <tr className="bg-gray-100">
            <th className="p-2 text-left">New Price</th>
            <th className="p-2 text-left">Old Price</th>
            <th className="p-2 text-left">Set By</th>
            <th className="p-2 text-center">Done At</th>
            <th className="p-2 text-center">Applied From</th>
          </tr>
        </thead>
        <tbody>
          {data.map((entry) => (
            <tr key={entry._id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="p-2 text-left">₹{entry.newPrice}</td>
              <td className="p-2 text-left">{entry.oldPrice ? `₹${entry.oldPrice}` : "-"}</td>
              <td className="p-2 text-left">{entry.setBy}</td>
              <td className="p-2 text-center">{formatDateandtime(entry.created_at)}</td>
              <td className="p-2 text-center">{formatDate(entry.appliedFrom)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );

  const TABS = [
    { key: "company", label: "Company History" },
    { key: "department", label: "Department History" },
    { key: "food", label: "Food History" },
    { key: "bus", label: "BusFare History" },
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Wage History</h3>
              <p className="text-sm mt-1">
                <span className="font-medium text-gray-800">Company Name:</span>{" "}
                <span className="text-gray-600">{company.name}</span>
              </p>
              <p className="text-sm mt-1">
                <span className="font-medium text-gray-800">Current Company Price:</span>{" "}
                <span className="text-gray-600 font-semibold">{currentprice ? `Rs ${currentprice}` : "-"}</span>
              </p>
            </div>
            <X className="h-5 w-5 cursor-pointer text-gray-500 hover:text-gray-700" onClick={onClose} />
          </div>

          <div className="flex gap-2 px-5 pt-3">
            {TABS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-4 py-1.5 rounded text-sm font-medium border transition-all ${
                  tab === key ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-3">
            {loading ? (
              <p className="text-sm text-gray-400 text-center py-10">Loading...</p>
            ) : (
              <>
                {tab === "company" && <PriceTable data={history} />}
                {tab === "food" && <PriceTable data={foodpricehistory} />}
                {tab === "bus" && <PriceTable data={buspricehistory} />}
                {tab === "department" && (
                  departments.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-10">No departments found.</p>
                  ) : (
                    <table className="w-full text-sm border-collapse">
                      <thead className="sticky top-0 bg-white shadow-md z-[50]">
                        <tr className="bg-gray-100">
                          <th className="p-2 text-left">Department Name</th>
                          <th className="p-2 text-center">Current Price</th>
                          <th className="p-2 text-right">View History</th>
                        </tr>
                      </thead>
                      <tbody>
                        {departments.map((dept) => (
                          <tr key={dept._id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="p-2 text-left">{dept.name}</td>
                            <td className="p-2 text-center">{dept.hourlyRate}</td>
                            <td className="p-2 text-right">
                              <span
                                onClick={() => setSelectedDept(dept)}
                                className="px-3 py-1 text-xs font-medium text-blue-600 cursor-pointer transition-all underline"
                              >
                                View
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {selectedDept && (
        <DepartmentHistoryModal department={selectedDept} onClose={() => setSelectedDept(null)} />
      )}
    </>
  );
}

// ── Department History Modal ───────────────────────────────────────────────────
function DepartmentHistoryModal({ department, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departmentcurrentprice, setdepartmentcurrentprice] = useState("");

  useEffect(() => {
    setLoading(true);
    axios
      .post(
        `${BASE_URL}/onDemandCompanies/price-history`,
        { entityType: "department", entityId: department.id },
        { headers: getHeaders() }
      )
      .then((res) => {
        if (res?.data?.success) {
          setdepartmentcurrentprice(res?.data?.currentPrice);
          setHistory(res?.data?.history || []);
        } else {
          toast.error(res?.data?.message || "Failed to load department history");
        }
      })
      .catch(() => toast.error("Failed to load department history"))
      .finally(() => setLoading(false));
  }, [department._id]);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  const formatDateandtime = (iso) =>
    new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold">Department Wage History</h3>
            <p className="text-sm">
              <span className="font-semibold text-gray-800">Department:</span>{" "}
              <span className="text-gray-600">{department.name}</span>
            </p>
            <p className="text-sm mt-1">
              <span className="font-semibold text-gray-800">Current Price:</span>{" "}
              <span className="text-gray-600 font-medium">
                {departmentcurrentprice ? `Rs ${departmentcurrentprice}` : "-"}
              </span>
            </p>
          </div>
          <X className="h-5 w-5 cursor-pointer text-gray-500 hover:text-gray-700" onClick={onClose} />
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <p className="text-sm text-gray-400 text-center py-10">Loading...</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">No history found.</p>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-white shadow-md z-[50]">
                <tr className="bg-gray-100">
                  <th className="p-2 text-left">New Price</th>
                  <th className="p-2 text-left">Old Price</th>
                  <th className="p-2 text-left">Set By</th>
                  <th className="p-2 text-center">Done At</th>
                  <th className="p-2 text-center">Applied From</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => (
                  <tr key={entry._id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-2 text-left">₹{entry.newPrice}</td>
                    <td className="p-2 text-center">{entry.oldPrice ? `₹${entry.oldPrice}` : "-"}</td>
                    <td className="p-2 text-left">{entry.setBy}</td>
                    <td className="p-2 text-center">{formatDateandtime(entry.created_at)}</td>
                    <td className="p-2 text-center">{formatDate(entry.appliedFrom)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


function CompanyModal({ mode, initial, locations, onClose, onSave }) {
  const [form, setForm] = useState(() => {
    const merged = { ...EMPTY_FORM, ...(initial || {}) };
    if (Array.isArray(initial?.accessories)) {
      merged.accessories = initial.accessories.map((a) => ({
        id: a.accessoriesId,
        name: "", // filled in once accessoryList loads
        price: a.accessoriesPrice,
      }));
    }
    return merged;
  });
  const [mailInput, setMailInput] = useState("");
  const [pinInput, setPinInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [appliedFrom, setAppliedFrom] = useState("");
  const [foodAppliedFrom, setFoodAppliedFrom] = useState("");
  const [busAppliedFrom, setBusAppliedFrom] = useState("");
  const fileRef = useRef();

  // ── Accessories state 

  const [accessoryList, setAccessoryList] = useState([]);
  const [accessoriesLoading, setAccessoriesLoading] = useState(false);
  const [accessoryOpen, setAccessoryOpen] = useState(false);
  const accessoryRef = useRef();

  const { fetchaccessories } = useSuperAdmin();

  
  useEffect(() => {
    (async () => {
      setAccessoriesLoading(true);
      try {
        const res = await fetchaccessories();
        setAccessoryList(res.data.accessories || []);
      } catch (err) {
        console.error("fetchaccessories error:", err.response?.status, err.response?.data, err.message);
        toast.error("Failed to load accessories list");
      } finally {
        setAccessoriesLoading(false);
      }
    })();
  }, []);

  // Backfill accessory names once the accessories list has loaded
  useEffect(() => {
    if (!accessoryList.length || !form.accessories?.length) return;
    const needsNames = form.accessories.some((a) => !a.name);
    if (!needsNames) return;

    setForm((p) => ({
      ...p,
      accessories: p.accessories.map((a) => {
        if (a.name) return a;
        const match = accessoryList.find((acc) => acc._id === a.id);
        return match ? { ...a, name: match.name } : a;
      }),
    }));
  }, [accessoryList]);

  // Close accessories dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (accessoryRef.current && !accessoryRef.current.contains(e.target)) {
        setAccessoryOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Only show active accessories in the picker
  const activeAccessories = accessoryList.filter((a) => a.isActive);

  const rateChanged     = mode === "edit" && String(form.HourlyRate) !== String(initial?.HourlyRate);
  const foodRateChanged = mode === "edit" && initial != null && String(form.FoodRate) !== String(initial.FoodRate);
  const busRateChanged  = mode === "edit" && initial != null && String(form.BusRate)  !== String(initial.BusRate);

  const [pinErrors, setPinErrors] = useState([]);

  const addPins = () => {
    const parts = pinInput.split(",").map((p) => p.trim()).filter(Boolean);
    if (/[^\d,\s]/.test(pinInput)) {
      setErrors((p) => ({ ...p, disabledPincodes: "Only numbers are allowed" }));
      return;
    }
    if (!parts.length) {
      setErrors((p) => ({ ...p, disabledPincodes: "Please enter at least one pincode" }));
      return;
    }
    const invalids = [];
    const duplicates = [];
    const toAdd = [];

    parts.forEach((p) => {
      if (!/^\d{6}$/.test(p)) {
        invalids.push(`"${p}" — must be exactly 6 digits`);
      } else if (form.disabledPincodes?.includes(p) || toAdd.includes(p)) {
        duplicates.push(`"${p}" — already added`);
      } else {
        toAdd.push(p);
      }
    });

    const allErrors = [...invalids, ...duplicates];
    setPinErrors(allErrors);
    if (toAdd.length) set("disabledPincodes", [...(form.disabledPincodes || []), ...toAdd]);
    if (!allErrors.length) setPinInput("");
    setErrors((p) => ({ ...p, disabledPincodes: "" }));
  };

  const set = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    setErrors((p) => ({ ...p, [key]: "" }));
  };

  const handleLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      setErrors((p) => ({ ...p, companyLogo: "Only JPG or PNG files are allowed" }));
      e.target.value = "";
      return;
    }
    const preview = URL.createObjectURL(file);
    setForm((p) => ({ ...p, logo: file, companyLogoPreview: preview }));
    setErrors((p) => ({ ...p, companyLogo: "" }));
  };

  const addMail = () => {
    const email = mailInput.trim();
    if (!email) { setErrors((p) => ({ ...p, toReportEmails: "Please enter an email" })); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setErrors((p) => ({ ...p, toReportEmails: "Invalid email format" })); return; }
    if (form.toReportEmails.includes(email)) { setErrors((p) => ({ ...p, toReportEmails: "Email already added" })); return; }
    set("toReportEmails", [...form.toReportEmails, email]);
    setMailInput("");
  };
  const removeMail = (idx) => set("toReportEmails", form.toReportEmails.filter((_, i) => i !== idx));

  const addPin = () => {
    const v = pinInput.trim();

    if (!v) {
      setErrors((p) => ({ ...p, disabledPincodes: "Please enter pincode" }));
      return;
    }
    if (v.length !== 6) {
      setErrors((p) => ({ ...p, disabledPincodes: "Pincode must be 6 digits" }));
      return;
    }
    if (form.disabledPincodes?.includes(v)) {
      setErrors((p) => ({ ...p, disabledPincodes: "Pincode already added" }));
      return;
    }

    set("disabledPincodes", [...(form.disabledPincodes || []), v]);
    setPinInput("");
    setErrors((p) => ({ ...p, disabledPincodes: "" }));
  };
  const removePin = (idx) => set("disabledPincodes", form.disabledPincodes.filter((_, i) => i !== idx));


  const toggleAccessory = (acc) => {
    const exists = form.accessories?.some((a) => a.id === acc._id);
    if (exists) {
      set("accessories", form.accessories.filter((a) => a.id !== acc._id));
    } else {
      set("accessories", [
        ...(form.accessories || []),
        { id: acc._id, name: acc.name, price: acc.defaultPrice ?? "" },
      ]);
    }
    setErrors((p) => ({ ...p, accessories: "" }));
  };

  const updateAccessoryPrice = (id, price) => {
    set("accessories", form.accessories.map((a) => (a.id === id ? { ...a, price } : a)));
  };

  const removeAccessory = (id) =>
    set("accessories", form.accessories.filter((a) => a.id !== id));

  const validate = () => {
    const e = {};
    if (!String(form.name || "").trim())                        e.name = "Company Name is required";
    else if (!form.companyLogoPreview && !form.logo)             e.companyLogo = "Company logo is required";
    else if (!String(form.location || "").trim())                e.location = "Location is required";
    else if (!String(form.address || "").trim())                 e.address = "Address is required";
    else if (String(form.address).trim().length < 10)            e.address = "Address must be at least 10 characters";
    else if (!String(form.latitude || "").trim())                e.latitude = "Latitude is required";
    else if (isNaN(Number(form.latitude)))                       e.latitude = "Latitude must be a number";
    else if (!String(form.longitude || "").trim())               e.longitude = "Longitude is required";
    else if (isNaN(Number(form.longitude)))                      e.longitude = "Longitude must be a number";
    else if (form.toReportEmails.length === 0)                   e.toReportEmails = "At least 1 report email is required";
    else if (!String(form.HourlyRate || "").trim())              e.HourlyRate = "Hourly Rate is required";
    else if (isNaN(Number(form.HourlyRate)))                     e.HourlyRate = "Hourly Rate must be a number";
    else if (!String(form.FoodRate || "").trim())                e.FoodRate = "Food Rate is required";
    else if (isNaN(Number(form.FoodRate)))                       e.FoodRate = "Food Rate must be a number";
    else if (Number(form.FoodRate) < 0)                          e.FoodRate = "Food Rate cannot be negative";
    else if (!String(form.BusRate || "").trim())                 e.BusRate = "Bus Rate is required";
    else if (isNaN(Number(form.BusRate)))                        e.BusRate = "Bus Rate must be a number";
    else if (!form.localWorkersAllowed && !form.disabledPincodes?.length) {
      e.disabledPincodes = "At least 1 disabled pincode is required when local workers are not allowed";
    }
    else if (Number(form.BusRate) < 0)                           e.BusRate = "Bus Rate cannot be negative";
    else if (rateChanged && !appliedFrom)                        e.appliedFrom = "Applied From date is required when changing Hourly Rate";
    else if (foodRateChanged && !foodAppliedFrom)                e.foodAppliedFrom = "Applied From date is required when changing Food Rate";
    else if (busRateChanged && !busAppliedFrom)                  e.busAppliedFrom = "Applied From date is required when changing Bus Rate";
    // accessories are optional — only validate price format IF the user selected any
    else if (form.accessories?.length && form.accessories.some((a) => !String(a.price || "").trim()))
      e.accessories = "Price is required for every selected Essentials";
    else if (form.accessories?.some((a) => isNaN(Number(a.price)) || Number(a.price) < 0))
      e.accessories = "Essentials price must be a valid, non-negative number";
    else if (!String(form.contactPersonName || "").trim())       e.contactPersonName = "Contact Person Name is required";
    else if (!String(form.contactPersonDesignation || "").trim()) e.contactPersonDesignation = "Designation is required";
    else if (!String(form.contactPersonNumber || "").trim())     e.contactPersonNumber = "Phone Number is required";
    else if (form.contactPersonNumber.length < 10)               e.contactPersonNumber = "Phone no. must be at least 10 digits";
    else if (!String(form.contactPersonEmail || "").trim())      e.contactPersonEmail = "Contact Email is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === "logo" && v instanceof File)                      fd.append("logo", v);
        else if (k === "toReportEmails")                            fd.append("toReportEmails", JSON.stringify(v));
        else if (k === "disabledPincodes")                          fd.append("disabledPincodes", JSON.stringify(v));
        else if (k === "accessories") {
          const accessoriesPayload = (v || []).map((a) => ({
            accessoriesId: a.id,
            accessoriesPrice: a.price,
          }));
          fd.append("accessories", JSON.stringify(accessoriesPayload));
        }
        else if (k === "location")                                  fd.append("locationId", v);
        else if (["isActive", "localWorkersAllowed", "allowNewWorkers", "allowVerifiedWorkersOnly","allowPartialHours"].includes(k))
          fd.append(k, String(v));
        else if (k !== "companyLogoPreview")                        fd.append(k, v ?? "");
      });
      if (rateChanged && appliedFrom)         fd.append("appliedFrom", appliedFrom);
      if (foodRateChanged && foodAppliedFrom) fd.append("foodAppliedFrom", foodAppliedFrom);
      if (busRateChanged && busAppliedFrom)   fd.append("busAppliedFrom", busAppliedFrom);

      const url = mode === "add"
        ? `${BASE_URL}/onDemandCompanies/add-new-company`
        : `${BASE_URL}/onDemandCompanies/edit-company/${initial.id}`;

      const res = await axios[mode === "add" ? "post" : "put"](url, fd, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      if (res?.data?.success) {
        toast.success(`Company ${mode === "add" ? "added" : "updated"} successfully`);
        onSave();
        onClose();
      } else {
        toast.error(res?.data?.message || "Failed to save");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // ── Shared date picker for "Applied From" fields ───────────────────────────
  const AppliedFromPicker = ({ label, errorKey, value, onChange }) => (
    <Field label={label} required error={errors[errorKey]}>
      <div className="relative">
        <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          type="date"
          value={value}
          onChange={(e) => { onChange(e.target.value); setErrors((p) => ({ ...p, [errorKey]: "" })); }}
          className={`w-full pl-8 pr-3 py-2 text-sm border rounded-lg bg-gray-50 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all ${
            errors[errorKey] ? "border-red-400 bg-red-50" : "border-gray-200"
          }`}
        />
      </div>
    </Field>
  );

  // ── Toggle buttons (Active/Inactive, Yes/No) 
  const ToggleButtons = ({ fieldKey, options }) => (
    <div className="flex gap-3">
      {options.map((s) => (
        <button
          key={s.label}
          onClick={() => set(fieldKey, s.value)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all h-7"
          style={{
            background:   form[fieldKey] === s.value ? (s.value ? "#DCFCE7" : "#FEE2E2") : "#F9FAFB",
            color:        form[fieldKey] === s.value ? (s.value ? "#16A34A" : "#DC2626") : "#6B7280",
            borderColor:  form[fieldKey] === s.value ? (s.value ? "#86EFAC" : "#FCA5A5") : "#E5E7EB",
          }}
        >
          <span
            className="w-2 h-2 rounded-full"
            style={{ background: form[fieldKey] === s.value ? (s.value ? "#16A34A" : "#DC2626") : "#D1D5DB" }}
          />
          {s.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-900">
            {mode === "add" ? "Add New Company" : "Edit Company"}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-all">
            <X size={16} color="#6B7280" />
          </button>
        </div>

        <div className="p-6 space-y-5">

          {/* Company Logo */}
          <Field label="Company Logo" required error={errors.companyLogo}>
            <div className="flex items-center gap-4">
              <div
                className={`w-16 h-16 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden cursor-pointer transition-all ${
                  errors.companyLogo ? "border-red-400" : "border-gray-200 hover:border-blue-400"
                }`}
                onClick={() => fileRef.current.click()}
              >
                {form.companyLogoPreview ? (
                  <img src={form.companyLogoPreview} alt="logo" className="w-full h-full object-cover" />
                ) : form.logo && typeof form.logo === "string" ? (
                  <img src={form.logo} alt="logo" className="w-full h-full object-cover" />
                ) : (
                  <Upload size={20} color={errors.companyLogo ? "#F87171" : "#D1D5DB"} />
                )}
              </div>
              <div>
                <button
                  onClick={() => fileRef.current.click()}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all text-gray-600"
                >
                  {form.companyLogoPreview || form.logo ? "Change Logo" : "Upload Logo"}
                </button>
                <p className="text-xs text-gray-400 mt-1">JPG or PNG only</p>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={handleLogo} />
              </div>
            </div>
          </Field>

          {/* Company Name */}
          <Field label="Company Name" required error={errors.name}>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Factrika" hasError={!!errors.name} />
          </Field>

          {/* Location */}
          <Field label="Location" required error={errors.location}>
            <div className="relative">
              <MapPin size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${form.location ? "text-black" : "text-gray-400"}`} />
              <select
                value={form.location}
                onChange={(e) => set("location", e.target.value)}
                className={`w-full h-7 pl-8 pr-8 text-sm border rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all appearance-none ${
                  form.location ? "text-black" : "text-gray-400"
                } ${errors.location ? "border-red-400 bg-red-50" : "border-gray-200"}`}
              >
                <option className="text-black" value="">Select Location</option>
                {locations.map((l) => <option className="text-black" key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </Field>

          {/* Address */}
          <Field label="Company Address" required error={errors.address}>
            <textarea
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              placeholder={"e.g. Rajendra place\nNorth delhi"}
              rows={2}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all resize-none ${
                errors.address ? "border-red-400 bg-red-50" : "border-gray-200"
              }`}
            />
          </Field>

          {/* Lat / Long */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Latitude" required error={errors.latitude}>
              <Input
                type="number"
                onKeyDown={(e) => { if (["e", "E", "+"].includes(e.key)) e.preventDefault(); }}
                value={form.latitude}
                onChange={(e) => set("latitude", e.target.value)}
                placeholder="e.g. 28.6139"
                hasError={!!errors.latitude}
              />
            </Field>
            <Field label="Longitude" required error={errors.longitude}>
              <Input
                type="number"
                onKeyDown={(e) => { if (["e", "E", "+"].includes(e.key)) e.preventDefault(); }}
                value={form.longitude}
                onChange={(e) => set("longitude", e.target.value)}
                placeholder="e.g. 77.2090"
                hasError={!!errors.longitude}
              />
            </Field>
          </div>

          {/* Report Mails */}
          <Field label="Report Mail(s)" required error={errors.toReportEmails}>
            {form.toReportEmails.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {form.toReportEmails.map((mail, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                    style={{ color: `#${COLOR}`, borderColor: "#BFDBFE", background: "#EFF6FF" }}
                  >
                    <Mail size={11} />
                    {mail}
                    <button onClick={() => removeMail(idx)} className="ml-0.5 hover:text-red-500 transition-colors">
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="email"
                value={mailInput}
                onChange={(e) => { setMailInput(e.target.value); setErrors((p) => ({ ...p, toReportEmails: "" })); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMail(); } }}
                placeholder="e.g. Enter email address"
                className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all ${
                  errors.toReportEmails ? "border-red-400 bg-red-50" : "border-gray-200"
                }`}
              />
              <button
                onClick={addMail}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-90 transition-all"
                style={{ background: `#${COLOR}` }}
              >
                <Plus size={15} /> Add
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {form.toReportEmails.length === 0
                ? "At least 1 email required"
                : `${form.toReportEmails.length} email${form.toReportEmails.length > 1 ? "s" : ""} added`}
            </p>
          </Field>

          {/* Hourly Rate */}
          <Field label="Hourly Rate" required error={errors.HourlyRate}>
            <Input
              type="number" min="0"
              onKeyDown={(e) => { if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }}
              value={form.HourlyRate}
              onChange={(e) => {
                set("HourlyRate", e.target.value);
                if (String(e.target.value) === String(initial?.HourlyRate)) {
                  setAppliedFrom(""); setErrors((p) => ({ ...p, appliedFrom: "" }));
                }
              }}
              placeholder="e.g. 150"
              hasError={!!errors.HourlyRate}
            />
          </Field>
          {rateChanged && (
            <AppliedFromPicker
              label="Hourly Rate Applied From" errorKey="appliedFrom"
              value={appliedFrom} onChange={setAppliedFrom}
            />
          )}

          {/* Food Rate */}
          <Field label="Food Rate" required error={errors.FoodRate}>
            <Input
              type="number" min="0"
              onKeyDown={(e) => { if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }}
              value={form.FoodRate}
              onChange={(e) => {
                set("FoodRate", e.target.value);
                if (String(e.target.value) === String(initial?.FoodRate)) {
                  setFoodAppliedFrom(""); setErrors((p) => ({ ...p, foodAppliedFrom: "" }));
                }
              }}
              placeholder="e.g. 20"
              hasError={!!errors.FoodRate}
            />
          </Field>
          {foodRateChanged && (
            <AppliedFromPicker
              label="Food Rate Applied From" errorKey="foodAppliedFrom"
              value={foodAppliedFrom} onChange={setFoodAppliedFrom}
            />
          )}

          {/* Bus Rate */}
          <Field label="Bus Rate"  error={errors.BusRate}>
            <Input
              type="number" min="0"
              onKeyDown={(e) => { if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }}
              value={form.BusRate}
              onChange={(e) => {
                set("BusRate", e.target.value);
                if (String(e.target.value) === String(initial?.BusRate)) {
                  setBusAppliedFrom(""); setErrors((p) => ({ ...p, busAppliedFrom: "" }));
                }
              }}
              placeholder="e.g. 10"
              hasError={!!errors.BusRate}
            />
          </Field>
          {busRateChanged && (
            <AppliedFromPicker
              label="Bus Rate Applied From" errorKey="busAppliedFrom"
              value={busAppliedFrom} onChange={setBusAppliedFrom}
            />
          )}

          {/* Local Worker Allowed */}
          <Field label="Local Worker Allowed" required>
            <div className="flex gap-3">
              {[{ label: "Yes", value: true }, { label: "No", value: false }].map((s) => (
                <button
                  key={s.label}
                  onClick={() => {
                    set("localWorkersAllowed", s.value);
                    if (s.value) setErrors((p) => ({ ...p, disabledPincodes: "" }));
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all h-7"
                  style={{
                    background:  form.localWorkersAllowed === s.value ? (s.value ? "#DCFCE7" : "#FEE2E2") : "#F9FAFB",
                    color:       form.localWorkersAllowed === s.value ? (s.value ? "#16A34A" : "#DC2626") : "#6B7280",
                    borderColor: form.localWorkersAllowed === s.value ? (s.value ? "#86EFAC" : "#FCA5A5") : "#E5E7EB",
                  }}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: form.localWorkersAllowed === s.value ? (s.value ? "#16A34A" : "#DC2626") : "#D1D5DB" }}
                  />
                  {s.label}
                </button>
              ))}
            </div>
          </Field>



          {/* Allow Only Existing Candidates */}
          <Field label="Allow New Workers" required>
            <ToggleButtons
              fieldKey="allowNewWorkers"
              options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
            />
          </Field>

                    <Field label="Allow Partial Hours" required>
            <ToggleButtons
              fieldKey="allowPartialHours"
              options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
            />
          </Field>

          {/* Allow Only Account Verified Workers */}
          <Field label="Allow Account Verified Workers Only" required>
            <ToggleButtons
              fieldKey="allowVerifiedWorkersOnly"
              options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
            />
          </Field>

          {/* Pincode(s) */}
          <Field
            label="Disabled Pincode(s)"
            required={!form.localWorkersAllowed}
            error={errors.disabledPincodes}
          >
            {form.disabledPincodes?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {form.disabledPincodes.map((pin, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                    style={{ color: "#1D4ED8", borderColor: "#BFDBFE", background: "#EFF6FF" }}
                  >
                    <MapPin size={11} />
                    {pin}
                    <button onClick={() => removePin(idx)} className="ml-0.5 hover:text-red-500 transition-colors">
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <textarea
                value={pinInput}
                onChange={(e) => {
                  setPinInput(e.target.value);
                  setPinErrors([]);
                  setErrors((p) => ({ ...p, disabledPincodes: "" }));
                }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addPins(); } }}
                placeholder={"Enter pincodes separated by commas\ne.g. 110001, 400001, 560001"}
                rows={2}
                className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all resize-none ${
                  errors.disabledPincodes || pinErrors.length ? "border-red-400 bg-red-50" : "border-gray-200"
                }`}
              />
              <button
                onClick={addPins}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-90 transition-all self-start"
                style={{ background: `#${COLOR}` }}
              >
                <Plus size={15} /> Add
              </button>
            </div>

            {pinErrors.length > 0 && (
              <div className="mt-2 border border-dashed border-red-300 bg-red-50 rounded-lg px-3 py-2">
                <p className="text-xs font-semibold text-red-600 mb-1">Fix these before adding:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {pinErrors.map((err, i) => (
                    <li key={i} className="text-xs text-red-500">{err}</li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-gray-400 mt-1">
              {!form.disabledPincodes?.length
                ? !form.localWorkersAllowed
                  ? "At least 1 pincode required"
                  : "Optional — add pincodes to disable"
                : `${form.disabledPincodes.length} pincode${form.disabledPincodes.length > 1 ? "s" : ""} added`}
            </p>
          </Field>

          {/* Accessories (optional) */}
          <Field label="Essentials" error={errors.accessories}>
            <div className="relative" ref={accessoryRef}>
              <button
                type="button"
                onClick={() => setAccessoryOpen((o) => !o)}
                disabled={accessoriesLoading}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none transition-all disabled:opacity-60 ${
                  errors.accessories ? "border-red-400 bg-red-50" : "border-gray-200"
                }`}
              >
                <span className={form.accessories?.length ? "text-black" : "text-gray-400"}>
                  {accessoriesLoading
                    ? "Loading Essentials..."
                    : form.accessories?.length
                    ? `${form.accessories.length} essential${form.accessories.length > 1 ? "s" : "y"} selected`
                    : "Select Essentials"}
                </span>
                {accessoriesLoading ? (
                  <Loader2 size={13} className="animate-spin text-gray-400" />
                ) : (
                  <ChevronDown size={13} className="text-gray-400" />
                )}
              </button>

              {accessoryOpen && !accessoriesLoading && (
                <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                  {activeAccessories.length ? (
                    activeAccessories.map((acc) => {
                      const checked = form.accessories?.some((a) => a.id === acc._id);
                      return (
                        <label
                          key={acc._id}
                          className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={!!checked}
                            onChange={() => toggleAccessory(acc)}
                            className="accent-blue-600 flex-shrink-0"
                          />
                          <div className="w-7 h-7 rounded-md border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0">
                            {acc.logo ? (
                              <img src={acc.logo} alt={acc.name} className="w-full h-full object-cover" />
                            ) : (
                              <Upload size={12} className="text-gray-300" />
                            )}
                          </div>
                          <span className="flex-1 truncate">{acc.name}</span>
                          {acc.defaultPrice !== undefined && acc.defaultPrice !== null && (
                            <span className="text-xs text-gray-400 flex-shrink-0">
                              ₹{Number(acc.defaultPrice).toFixed(0)}
                            </span>
                          )}
                        </label>
                      );
                    })
                  ) : (
                    <p className="px-3 py-2 text-xs text-gray-400">No Essentials found</p>
                  )}
                </div>
              )}
            </div>

            {form.accessories?.length > 0 && (
              <div className="mt-1 space-y-1">
                {form.accessories.map((a) => (
                  <div key={a.id} className="flex items-center py-0.5 px-0.5">
                    <span className="flex-1 text-sm font-medium text-gray-700">{a.name || "—"}</span>
                    <input
                      type="number"
                      min="0"
                      onKeyDown={(e) => { if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }}
                      value={a.price}
                      onChange={(e) => updateAccessoryPrice(a.id, e.target.value)}
                      placeholder="Enter Price"
                      className="w-28 px-2 py-1 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-400"
                    />
                    <button onClick={() => removeAccessory(a.id)} className="text-gray-400 mx-1">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-gray-400 mt-1">
              {!form.accessories?.length
                ? "Select Essentials"
                : `${form.accessories.length} essentia${form.accessories.length > 1 ? "s" : "y"} selected`}
            </p>
          </Field>

          {/* Minimum Qualification */}
          <Field label="Minimum Qualification" error={errors.minimumQualification}>
            <div className="relative">
              <select
                value={form.minimumQualification}
                onChange={(e) => { set("minimumQualification", e.target.value); setErrors((p) => ({ ...p, minimumQualification: "" })); }}
                className={`w-full h-7 pl-3 pr-8 text-sm border rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all appearance-none ${
                  form.minimumQualification ? "text-black" : "text-gray-400"
                } ${errors.minimumQualification ? "border-red-400 bg-red-50" : "border-gray-200"}`}
              >
                <option className="text-black" value="">Select Qualification</option>
                {QUALIFICATIONS.map((q) => (
                  <option className="text-black" key={q} value={q}>{q}</option>
                ))}
              </select>
              <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </Field>

          {/* Status */}
          <Field label="Status" required>
            <ToggleButtons
              fieldKey="isActive"
              options={[{ label: "Active", value: true }, { label: "Inactive", value: false }]}
            />
          </Field>

          {/* Contact Person */}
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Contact Person</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Contact Person Name" required error={errors.contactPersonName}>
                  <Input value={form.contactPersonName} onChange={(e) => set("contactPersonName", e.target.value)} placeholder="e.g. Aditya" hasError={!!errors.contactPersonName} />
                </Field>
                <Field label="Designation" required error={errors.contactPersonDesignation}>
                  <Input value={form.contactPersonDesignation} onChange={(e) => set("contactPersonDesignation", e.target.value)} placeholder="e.g. Manager" hasError={!!errors.contactPersonDesignation} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Phone Number" required error={errors.contactPersonNumber}>
                  <Input
                    type="tel"
                    value={form.contactPersonNumber}
                    onChange={(e) => { if (/^\d{0,10}$/.test(e.target.value)) set("contactPersonNumber", e.target.value); }}
                    placeholder="e.g. 9876543210"
                    maxLength={10}
                    hasError={!!errors.contactPersonNumber}
                  />
                </Field>
                <Field label="Contact Email" required error={errors.contactPersonEmail}>
                  <Input type="email" value={form.contactPersonEmail} onChange={(e) => set("contactPersonEmail", e.target.value)} placeholder="e.g. abc@gmail.com" hasError={!!errors.contactPersonEmail} />
                </Field>
              </div>
            </div>
          </div>

        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60 transition-all"
            style={{ background: `#${COLOR}` }}
          >
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : mode === "add" ? "Add Company" : "Save Changes"}
          </button>
        </div>

      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function ManageCompanies() {
  const [companies, setCompanies] = useState([]);
  const [alllocations, setallLocations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [wageHistoryCompany, setWageHistoryCompany] = useState(null);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/lenskart/locations`, {
        headers: getHeaders(),
        params: { isActive: false },
      });
      if (res?.data?.success && Array.isArray(res?.data?.locations)) {
        setallLocations(
          res.data.locations
            .map((item) => ({ id: item._id || item.id, name: item.name || item.locationName || "" }))
            .filter((l) => l.name)
        );
        setLocations(
          res.data.locations
            .filter((item) => item.isActive !== false)
            .map((item) => ({ id: item._id, name: item.name || item.locationName || "" }))
            .filter((l) => l.name)
        );
      }
    } catch (err) {
      console.error("Failed to load locations", err);
    }
  }, []);

const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/OnDemandCompanies/list-companies/${selectedLocation}`, {
        headers: getHeaders(),
        params: { isActive: false },
      });
      if (res?.data?.count === 0) toast.success("No company found for this location");
      if (res?.data?.success && Array.isArray(res?.data?.companies)) {
        setCompanies(res.data.companies.map((item) => ({
          id: item._id || item.id,
          name: item.name || "",
          logo: item.logo || null,
          address: item.address || "",
          latitude: item.latitude || "",
          longitude: item.longitude || "",
          toReportEmails: Array.isArray(item.reportEmails) ? item.reportEmails : (item.reportEmails ? [item.reportEmails] : []),
          HourlyRate: item.hourlyRate || "",
          FoodRate: item.foodRate || "",
          BusRate: item.busRate || "",
          isActive: item.isActive === true || item.isActive === "true",
          contactPersonName: item.contactPersonName || "",
          contactPersonDesignation: item.contactPersonDesignation || "",
          contactPersonNumber: item.contactPersonNumber || "",
          contactPersonEmail: item.contactPersonEmail || "",
          location: item.locationId || "",
          companycode: item.companyCode || "",
          location_name: item.locationName || "",
          localWorkersAllowed: item.localWorkersAllowed ?? true,
          allowNewWorkers: item.allowNewWorkers ?? true,
          allowVerifiedWorkersOnly: item.allowVerifiedWorkersOnly ?? false,
          allowPartialHours: item.allowPartialHours ?? false,
          disabledPincodes: Array.isArray(item.disabledPincodes) ? item.disabledPincodes : [],
          minimumQualification: item.minimumQualification || "",
          accessories: Array.isArray(item.accessories) ? item.accessories : [], // ← added
        })));
      }
    } catch (err) {
      toast.error("Failed to load companies");
    } finally {
      setLoading(false);
    }
  }, [selectedLocation]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);
  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  return (
    <div className="p-6" style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Manage Companies</h2>
          <p className="text-s text-gray-400">{loading ? "Loading..." : `Total Companies : ${companies.length}`}</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center justify-center gap-1"
        >
          Add Company
        </button>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative w-full">
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="pl-8 w-full pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 appearance-none cursor-pointer"
          >
            <option value="all">All Locations</option>
            {alllocations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      <div className="w-full">
        <div className="max-h-[70vh] overflow-y-auto overflow-x-auto relative">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-white shadow-md z-[50]">
              <tr className="bg-gray-100">
                <th className="p-2 text-left">Company</th>
                <th className="p-2 text-center">Status</th>
                <th className="p-2 text-center">Wages History</th>
                <th className="p-2 text-center">Edit</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="py-16 text-center">
                    <Loader2 size={28} className="animate-spin mx-auto mb-3" style={{ color: `#${COLOR}` }} />
                    <p className="text-sm text-gray-400">Loading companies...</p>
                  </td>
                </tr>
              ) : companies.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-16 text-center">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3">
                      <Building2 size={24} color={`#${COLOR}`} />
                    </div>
                    <p className="text-sm text-gray-500 font-medium">
                      {selectedLocation !== "all" ? "No companies match your filters." : "No companies yet."}
                    </p>
                  </td>
                </tr>
              ) : (
                companies.map((row) => (
                  <tr key={row.id} className="border-b hover:bg-gray-50">
                    <td className="p-2 text-left min-w-[600px]">
                      <div className="flex items-center gap-2">
                        {row.logo ? (
                          <img src={row.logo} alt={row.name} className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-gray-200" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-gray-400">{row.name?.charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                        <div>
                          <div className={`font-medium ${row.isActive ? "text-gray-800" : "text-gray-400"}`}>
                            {row.name} {row.companycode ? `(${row.companycode})` : ""}
                          </div>
                          <div className="text-xs text-gray-400">{row.location_name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-2 text-center">
                      <span
                        className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold w-fit mx-auto"
                        style={{ background: row.isActive ? "#DCFCE7" : "#FEE2E2", color: row.isActive ? "#16A34A" : "#DC2626" }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: row.isActive ? "#16A34A" : "#DC2626" }} />
                        {row.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="p-2 text-center">
                      <span
                        onClick={() => setWageHistoryCompany(row)}
                        className="text-xs font-semibold underline cursor-pointer"
                        title="View wage history"
                      >
                        Show
                      </span>
                    </td>
                    <td className="p-2 text-center">
                      <button
                        onClick={() => setEditCompany(row)}
                        className="p-1.5 rounded-lg hover:bg-blue-50 transition-all mx-auto block"
                        title="Edit company"
                      >
                        <Edit size={14} color={`#${COLOR}`} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <CompanyModal mode="add" locations={locations} onClose={() => setShowAddModal(false)} onSave={fetchCompanies} />
      )}
      {editCompany && (
        <CompanyModal mode="edit" initial={editCompany} locations={locations} onClose={() => setEditCompany(null)} onSave={fetchCompanies} />
      )}
      {wageHistoryCompany && (
        <WageHistoryModal company={wageHistoryCompany} onClose={() => setWageHistoryCompany(null)} />
      )}

    </div>
  );
}